import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import AppPaginationBar from "components/AppPaginationBar";
import { Delete, Edit, Save, Close, Refresh, Add, Visibility } from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { usePageTitle } from "contexts/PageTitleContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";

// ─── STEP 1: Import the config file ───────────────────────────────────────────
import buttonConfig from "../../config/ConfigAccessButton";

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.875rem",
  padding: "12px 16px",
  "&.header": {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TableCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Muted = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const ImageContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
});

const Users = () => {
  const theme = useTheme();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  const { showDeleteConfirmation } = useDeleteConfirmation();

  // ─── STEP 2: Set up permission helpers ────────────────────────────────────────
  //
  // Config shape expected in ConfigAccessButton.js:
  //   "Users": {
  //     add_user: true | false,
  //     edit:     true | false,
  //     delete:   true | false,
  //   }
  //
  const usersConfig = buttonConfig["Users"] || {};
  const can = (action) => !!usersConfig[action];

  const [errors, setErrors] = useState({
    mobile_number: '',
    email: ''
  });

  // Loading state
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingUserTypes, setLoadingUserTypes] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    user_type: "",
    first_name: "",
    last_name: "",
    mobile_number: "",
    email: "",
    pincode: "",
    country: "",
    state: "",
    district: "",
    tehsil: "",
    village: "",
    password: "",
    type_of_user: "",
    status: "Active",
    company: [],
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [categoryDraftSelection, setCategoryDraftSelection] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page_number: null,
    previous_page_number: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];

  const validateMobile = (value) => {
    if (!value) return 'Mobile number is required';
    if (!/^[6-9]\d{9}$/.test(value)) return 'Enter valid 10-digit mobile number';
    return '';
  };

  const validateEmail = (value) => {
    if (!value) return 'Email is required';
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) return 'Enter a valid email address';
    return '';
  };

  useEffect(() => {
    setPageTitle("Users");
    fetchUsers(1, 10);
    fetchUserTypes();
    fetchCompanies();
  }, [setPageTitle]);

  const fetchUsers = async (pageParam = 1, pageSizeParam = 10) => {
    setLoading(true);
    try {
      const url = `${API_ENDPOINTS.Get_Users}${API_ENDPOINTS.Get_Users.includes('?') ? '&' : '?'}page=${pageParam}&page_size=${pageSizeParam}`;
      const res = await apiService.get(url);

      const list = res?.data?.data || res?.data || [];
      const active = Array.isArray(list) ? list.filter(u => !u.is_deleted) : [];
      setUsers(active);

      const paginationData = res?.pagination || res?.data?.pagination || {};
      if (Object.keys(paginationData).length > 0) {
        setPagination({
          current_page: paginationData.current_page ?? pageParam,
          page_size: paginationData.page_size ?? pageSizeParam,
          total_items: paginationData.total_items ?? 0,
          total_pages: paginationData.total_pages ?? 1,
          has_next: paginationData.has_next ?? false,
          has_previous: paginationData.has_previous ?? false,
          next_page_number: paginationData.next_page_number ?? null,
          previous_page_number: paginationData.previous_page_number ?? null,
        });
        setCurrentPage(paginationData.current_page ?? pageParam);
        setPageSize(paginationData.page_size ?? pageSizeParam);
      } else {
        setPagination(prev => ({ ...prev, total_items: active.length, total_pages: 1, current_page: 1, page_size: pageSizeParam }));
      }
    } catch (err) {
      console.error('Error fetching users', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUsersPageChange = (_, value) => {
    setCurrentPage(value);
    fetchUsers(value, pageSize);
  };

  const handleUsersPageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    fetchUsers(1, newSize);
  };

  const fetchUserTypes = async () => {
    setLoadingUserTypes(true);
    try {
      const res = await apiService.get(API_ENDPOINTS.Get_User_types);
      const list = res?.data?.data || res?.data || [];
      setUserTypes(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetching user types', err);
    } finally {
      setLoadingUserTypes(false);
    }
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const res = await apiService.get(API_ENDPOINTS.GET_company_choices);
      console.log('Companies API Response:', res);

      let companiesList = [];

      if (res?.data?.data?.Company && Array.isArray(res.data.data.Company)) {
        companiesList = res.data.data.Company;
      } else if (res?.data?.Company && Array.isArray(res.data.Company)) {
        companiesList = res.data.Company;
      } else if (res?.data?.data && Array.isArray(res.data.data)) {
        companiesList = res.data.data;
      } else if (res?.data && Array.isArray(res.data)) {
        companiesList = res.data;
      } else if (Array.isArray(res)) {
        companiesList = res;
      }

      setCompanies(companiesList);

      if (companiesList.length === 0) {
        console.log('No companies found in response');
      } else {
        console.log(`Loaded ${companiesList.length} companies`);
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const normalizeCompanySelection = (companyValue) => {
    if (Array.isArray(companyValue)) {
      return companyValue
        .map((item) => (typeof item === "object" ? item?.id : item))
        .filter((item) => item !== null && item !== undefined && item !== "");
    }
    if (companyValue && typeof companyValue === "object") {
      return companyValue.id ? [companyValue.id] : [];
    }
    if (companyValue !== null && companyValue !== undefined && companyValue !== "") {
      return [companyValue];
    }
    return [];
  };

  const getCompanyNameById = (companyId) => {
    const selectedCompany = companies.find(
      (company) => String(company.id) === String(companyId)
    );
    return (
      selectedCompany?.name ||
      selectedCompany?.company_name ||
      selectedCompany?.title ||
      String(companyId)
    );
  };

  const parseCompanyIdsFromUser = (user) => {
    const ids = [];

    if (typeof user?.company_ids === "string") {
      ids.push(...user.company_ids.split(",").map((item) => item.trim()).filter(Boolean));
    } else if (Array.isArray(user?.company_ids)) {
      ids.push(...user.company_ids);
    }

    if (Array.isArray(user?.company)) {
      ids.push(...user.company.map((item) => (typeof item === "object" ? item?.id : item)));
    } else if (user?.company && typeof user.company === "object") {
      ids.push(user.company.id);
    } else if (user?.company !== null && user?.company !== undefined && user?.company !== "") {
      ids.push(user.company);
    }

    if (Array.isArray(user?.user_company)) {
      ids.push(...user.user_company.map((item) => (typeof item === "object" ? item?.id : item)));
    } else if (user?.user_company && typeof user.user_company === "object") {
      ids.push(user.user_company.id);
    } else if (user?.user_company !== null && user?.user_company !== undefined && user?.user_company !== "") {
      ids.push(user.user_company);
    }

    if (Array.isArray(user?.extra_companies)) {
      ids.push(...user.extra_companies.map((item) => (typeof item === "object" ? item?.id : item)));
    } else if (user?.extra_companies && typeof user.extra_companies === "object") {
      ids.push(user.extra_companies.id);
    } else if (user?.extra_companies !== null && user?.extra_companies !== undefined && user?.extra_companies !== "") {
      ids.push(user.extra_companies);
    }

    return [...new Set(ids.map((item) => String(item).trim()).filter(Boolean))];
  };

  const getCompanyNamesForUser = (user) => {
    const ids = parseCompanyIdsFromUser(user);
    if (ids.length === 0) return [];

    const extraCompanyNameById = new Map(
      (Array.isArray(user?.extra_companies) ? user.extra_companies : [])
        .map((company) => ({
          id: company?.id,
          name:
            company?.name ||
            company?.company_name ||
            company?.title ||
            (company?.id !== null && company?.id !== undefined ? String(company.id) : ""),
        }))
        .filter((item) => item.id !== null && item.id !== undefined && item.name)
        .map((item) => [String(item.id), item.name])
    );

    return ids.map((id) => extraCompanyNameById.get(String(id)) || getCompanyNameById(id));
  };

  const getUserTypeDisplayName = (user) => {
    if (user?.user_type?.name) return user.user_type.name;

    const userTypeId =
      user?.user_type?.id ??
      user?.user_type ??
      user?.user_type_id;

    if (userTypeId !== null && userTypeId !== undefined && userTypeId !== "") {
      const matchedType = userTypes.find((type) => String(type.id) === String(userTypeId));
      if (matchedType?.name) return matchedType.name;
    }

    if (user?.type_of_user) return user.type_of_user;
    return "N/A";
  };

  const getUserTypeIdForForm = (user) => {
    const directId =
      user?.user_type?.id ??
      user?.user_type_id ??
      (typeof user?.user_type === "number" || typeof user?.user_type === "string"
        ? user.user_type
        : "");

    if (directId !== null && directId !== undefined && directId !== "") {
      return String(directId);
    }

    if (user?.type_of_user) {
      const matchedType = userTypes.find(
        (type) =>
          String(type?.name || "").toLowerCase() === String(user.type_of_user).toLowerCase()
      );
      if (matchedType?.id !== undefined && matchedType?.id !== null) {
        return String(matchedType.id);
      }
    }

    return "";
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingId(user.id);
      const companyIds = parseCompanyIdsFromUser(user);

      setFormData({
        user_type: getUserTypeIdForForm(user),
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        mobile_number: user.mobile_number || "",
        email: user.email || "",
        pincode: user.pincode || "",
        country: user.country || "",
        state: user.state || "",
        district: user.district || "",
        tehsil: user.tehsil || "",
        village: user.village || "",
        password: "",
        type_of_user: user.type_of_user || "",
        status: user.status || "Active",
        company: companyIds,
      });
      setCategoryDraftSelection(companyIds);
      setErrors({ mobile_number: '', email: '' });
    } else {
      setEditingId(null);
      setFormData({
        user_type: "",
        first_name: "",
        last_name: "",
        mobile_number: "",
        email: "",
        pincode: "",
        country: "",
        state: "",
        district: "",
        tehsil: "",
        village: "",
        password: "",
        type_of_user: "",
        status: "Active",
        company: [],
      });
      setCategoryDraftSelection([]);
      setErrors({ mobile_number: '', email: '' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setCategoryMenuOpen(false);
    setCategoryDraftSelection([]);
    setErrors({ mobile_number: '', email: '' });
  };

  const handleCategoryMenuOpen = () => {
    setCategoryDraftSelection(
      normalizeCompanySelection(formData.company).map((companyId) => String(companyId))
    );
    setCategoryMenuOpen(true);
  };

  const handleCategoryMenuClose = () => {
    setCategoryMenuOpen(false);
  };

  const handleCategoryCancel = () => {
    setCategoryDraftSelection(
      normalizeCompanySelection(formData.company).map((companyId) => String(companyId))
    );
    setCategoryMenuOpen(false);
  };

  const handleCategoryDone = () => {
    const normalizedCompanyIds = normalizeCompanySelection(categoryDraftSelection).map(
      (companyId) => String(companyId)
    );
    setFormData((prev) => ({ ...prev, company: normalizedCompanyIds }));
    setCategoryDraftSelection(normalizedCompanyIds);
    setCategoryMenuOpen(false);
  };

  const allCategoryIds = companies.map((company) => String(company.id));
  const areAllCategoriesSelected =
    allCategoryIds.length > 0 &&
    allCategoryIds.every((companyId) =>
      categoryDraftSelection.some((selectedId) => String(selectedId) === companyId)
    );

  const handleToggleSelectAllCategories = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (areAllCategoriesSelected) {
      setCategoryDraftSelection([]);
      return;
    }
    setCategoryDraftSelection(allCategoryIds);
  };

  const handleToggleSingleCategory = (event, companyId) => {
    event.preventDefault();
    event.stopPropagation();
    const normalizedId = String(companyId);
    setCategoryDraftSelection((prev) => {
      const exists = prev.some((selectedId) => String(selectedId) === normalizedId);
      if (exists) {
        return prev.filter((selectedId) => String(selectedId) !== normalizedId);
      }
      return [...prev, normalizedId];
    });
  };

  const handleSave = async () => {
    const mobileError = validateMobile(formData.mobile_number);
    const emailError = validateEmail(formData.email);

    if (mobileError) {
      setErrors(prev => ({ ...prev, mobile_number: mobileError }));
      toast.showError(mobileError);
      return;
    }

    if (emailError) {
      setErrors(prev => ({ ...prev, email: emailError }));
      toast.showError(emailError);
      return;
    }

    if (!formData.first_name.trim()) {
      toast.showError('First name is required');
      return;
    }

    setSaving(true);
    try {
      const selectedCompanyIds = Array.isArray(formData.company)
        ? formData.company
            .map((companyId) => Number(companyId))
            .filter((companyId) => Number.isFinite(companyId) && companyId > 0)
        : [];

      const payload = {
        user_type: Number(formData.user_type) || null,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name?.trim() || null,
        mobile_number: formData.mobile_number?.trim() || null,
        email: formData.email?.trim() || null,
        pincode: formData.pincode || null,
        country: formData.country || null,
        state: formData.state || null,
        district: formData.district || null,
        tehsil: formData.tehsil || null,
        village: formData.village || null,
        password: formData.password || undefined,
        type_of_user: formData.type_of_user || null,
        status: formData.status || 'Active',
        company: selectedCompanyIds.length ? selectedCompanyIds[0] : null,
        company_ids: selectedCompanyIds.length ? selectedCompanyIds.join(",") : null,
      };

      let res;
      if (editingId) {
        res = await apiService.put(API_ENDPOINTS.Update_Users, { ...payload, user_id: editingId });
      } else {
        res = await apiService.post('/m2/user/add-user/', payload);
      }

      console.log('Save user response', res);

      if (res?.success) {
        if (editingId) {
          toast.showUpdateSuccess('User');
        } else {
          toast.showAddSuccess('User');
        }
        handleCloseDialog();
        await fetchUsers(currentPage, pageSize);
      } else {
        toast.showError(res?.message || 'Failed to save user');
      }
    } catch (err) {
      console.error('Error saving user', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to save user';
      toast.showError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    const confirmed = await showDeleteConfirmation({
      title: 'Delete User',
      message: 'Are you sure you want to delete this user?',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });

    if (!confirmed) return;

    setDeletingId(id);
    try {
      const res = await apiService.delete(`${API_ENDPOINTS.Delete_Users}?user_id=${id}`);
      console.log('Delete response', res);
      if (res?.success) {
        toast.showDeleteSuccess('User');
        setUsers(prev => prev.filter(u => u.id !== id));
        await fetchUsers(currentPage, pageSize);
      } else {
        toast.showDeleteError('User');
      }
    } catch (err) {
      console.error('Error deleting user', err);
      toast.showDeleteError('User');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (user, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    handleOpenDialog(user);
  };

  return (
    <PageContainer>
      <HeaderCard>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Users
          </Typography>
          <Muted variant="body2">Manage application users</Muted>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Rows</InputLabel>
            <Select value={pageSize} label="Rows" onChange={handleUsersPageSizeChange}>
              {pageSizeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ── MODIFIED: "Add User" button — visible but disabled when no permission ── */}
          <Tooltip title={can('add_user') ? "" : "No permission to add user"}>
            <span>
              <Button
                startIcon={<Add />}
                variant="contained"
                onClick={() => can('add_user') && handleOpenDialog()}
                disabled={!can('add_user')}
              >
                Add User
              </Button>
            </span>
          </Tooltip>

          <Button
            startIcon={<Refresh />}
            variant="outlined"
            onClick={() => fetchUsers(currentPage, pageSize)}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
      </HeaderCard>

      <TableCard>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 3,
            borderRadius: 2,
            maxHeight: 500,
            overflow: 'auto',
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark" ? "#555 #1e1e1e" : "#c1c1c1 #f1f1f1",
            "&::-webkit-scrollbar": { height: 8, width: 8 },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: theme.palette.mode === "dark" ? "#777" : "#a8a8a8",
            },
          }}
        >
          <Table stickyHeader>
            <TableHead sx={{ position: "sticky", top: 0, zIndex: 20 }}>
              <TableRow>
                <StyledTableCell className="header" align="center" sx={{ width: '60px' }}>Sr No</StyledTableCell>
                <StyledTableCell className="header" align="center">Name</StyledTableCell>
                <StyledTableCell className="header" align="center">Mobile</StyledTableCell>
                <StyledTableCell className="header" align="center">Email</StyledTableCell>
                <StyledTableCell className="header" align="center">User Type</StyledTableCell>
                <StyledTableCell className="header" align="center">Company</StyledTableCell>
                <StyledTableCell className="header" align="center">Status</StyledTableCell>
                <StyledTableCell
                  className="header"
                  sx={{
                    position: "sticky",
                    right: 0,
                    top: 0,
                    zIndex: 9999,
                    width: 150,
                    boxShadow: "-4px 0 6px rgba(0,0,0,0.15) !important",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "light" ? "rgba(246, 248, 247)" : theme.palette.background.paper,
                  }}
                >
                  Actions
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" sx={{ mt: 1 }}>Loading users...</Typography>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No users found. Click "Add User" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user, index) => (
                  <StyledTableRow key={user.id}>
                    <StyledTableCell style={{ padding: "16px" }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {(currentPage - 1) * pageSize + index + 1}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell style={{ padding: "16px" }}>
                      <Typography variant="body1" fontWeight={600}>
                        {user.first_name} {user.last_name || ''}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2">{user.mobile_number || 'N/A'}</Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2">{user.email || 'N/A'}</Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2">{getUserTypeDisplayName(user)}</Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {(() => {
                        const companyNames = getCompanyNamesForUser(user);
                        if (companyNames.length === 0) {
                          return <Typography variant="body2">N/A</Typography>;
                        }
                        const visibleCompanies = companyNames.slice(0, 2);
                        const remainingCompanies = companyNames.slice(2);
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.75,
                              flexWrap: "wrap",
                              textAlign: "center",
                            }}
                          >
                            <Typography variant="body2">{visibleCompanies.join(", ")}</Typography>
                            {remainingCompanies.length > 0 && (
                              <Tooltip
                                arrow
                                placement="top"
                                title={
                                  <Box sx={{ py: 0.25 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                      More Companies
                                    </Typography>
                                    <Typography variant="body2">
                                      {remainingCompanies.join(", ")}
                                    </Typography>
                                  </Box>
                                }
                              >
                                <Chip
                                  label={`+${remainingCompanies.length}`}
                                  size="small"
                                  sx={{ height: 22, fontWeight: 600, cursor: "pointer" }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        );
                      })()}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Chip
                        label={user.status || 'N/A'}
                        size="small"
                        color={user.status === 'Active' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        right: 0,
                        zIndex: 4,
                        backgroundColor: theme.palette.background.paper,
                        display: "flex",
                        gap: 1,
                        minWidth: 150,
                        padding: "50px 20px",
                      }}
                    >
                      <Box display="flex" gap={1} justifyContent="center" alignItems="center">

                        {/* ── MODIFIED: Edit icon — disabled when can('edit') is false ── */}
                        <Tooltip title={can('edit') ? "Edit" : "No permission to edit"}>
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={(e) => can('edit') && handleEdit(user, e)}
                              disabled={!can('edit')}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* ── MODIFIED: Delete icon — disabled when can('delete') is false ── */}
                        <Tooltip title={can('delete') ? "Delete" : "No permission to delete"}>
                          <span>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={(e) => can('delete') && handleDelete(user.id, e)}
                              disabled={!can('delete') || deletingId === user.id}
                            >
                              {deletingId === user.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Delete fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>

                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>

      <AppPaginationBar
        pagination={pagination}
        pageSize={pageSize}
        onPageChange={handleUsersPageChange}
        itemLabel="users"
      />

      {/* ── Add/Edit Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{editingId ? 'Edit User' : 'Add User'}</Typography>
            <IconButton onClick={handleCloseDialog} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select
                value={formData.user_type}
                label="User Type"
                onChange={(e) => setFormData(prev => ({ ...prev, user_type: e.target.value }))}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {userTypes.map(ut => (
                  <MenuItem key={ut.id} value={ut.id}>{ut.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status || ""}
                label="Status"
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <TextField
            label="First Name"
            value={formData.first_name}
            onChange={(e) => {
              const lettersOnly = e.target.value.replace(/[^A-Za-z]/g, '');
              setFormData(prev => ({ ...prev, first_name: lettersOnly }));
            }}
            fullWidth
            required
          />
          <TextField
            label="Last Name"
            value={formData.last_name}
            onChange={(e) => {
              const lettersOnly = e.target.value.replace(/[^A-Za-z]/g, '');
              setFormData(prev => ({ ...prev, last_name: lettersOnly }));
            }}
            fullWidth
          />
            <TextField
              label="Mobile Number"
              value={formData.mobile_number}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setFormData(prev => ({ ...prev, mobile_number: value }));
                setErrors(prev => ({ ...prev, mobile_number: validateMobile(value) }));
              }}
              error={!!errors.mobile_number}
              helperText={errors.mobile_number}
              fullWidth
              required
              inputProps={{ maxLength: 10 }}
            />

            <TextField
              label="Email"
              value={formData.email}
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => ({ ...prev, email: value }));
                setErrors(prev => ({ ...prev, email: validateEmail(value) }));
              }}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              required
              placeholder="example@domain.com"
            />

            <FormControl fullWidth sx={{ gridColumn: 'span 2' }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryMenuOpen ? categoryDraftSelection : formData.company}
                label="Category"
                multiple
                open={categoryMenuOpen}
                onOpen={handleCategoryMenuOpen}
                onClose={handleCategoryMenuClose}
                renderValue={(selected) => {
                  const selectedNames = selected.map((companyId) => getCompanyNameById(companyId));
                  const visibleNames = selectedNames.slice(0, 2);
                  const remainingCount = selectedNames.length - visibleNames.length;
                  return remainingCount > 0
                    ? `${visibleNames.join(", ")}, +${remainingCount} more`
                    : visibleNames.join(", ");
                }}
                onChange={(e) => {
                  const { target: { value } } = e;
                  const selectedValues = typeof value === "string" ? value.split(",") : value;
                  setCategoryDraftSelection(
                    normalizeCompanySelection(selectedValues).map((companyId) => String(companyId))
                  );
                }}
                disabled={loadingCompanies}
              >
                {loadingCompanies ? (
                  <MenuItem disabled>Loading companies...</MenuItem>
                ) : companies.length === 0 ? (
                  <MenuItem disabled>No companies available</MenuItem>
                ) : (
                  <>
                    <MenuItem
                      onClick={handleToggleSelectAllCategories}
                      sx={{
                        justifyContent: "center",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        minHeight: 36,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          textDecoration: "underline",
                          textUnderlineOffset: "3px",
                          textDecorationColor: "warning.main",
                        }}
                      >
                        {areAllCategoriesSelected ? "Deselect All" : "Select All"}
                      </Typography>
                    </MenuItem>
                    {companies.map(company => (
                      <MenuItem
                        key={company.id}
                        value={String(company.id)}
                        onClick={(event) => handleToggleSingleCategory(event, company.id)}
                      >
                        <Checkbox
                          checked={categoryDraftSelection.some(
                            (companyId) => String(companyId) === String(company.id)
                          )}
                        />
                        {company.name || company.company_name || company.title || company.id}
                      </MenuItem>
                    ))}
                  </>
                )}
                {!loadingCompanies && companies.length > 0 && (
                  <ListSubheader
                    component="div"
                    disableSticky
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      zIndex: 1,
                      backgroundColor: "background.paper",
                      borderTop: "1px solid",
                      borderColor: "divider",
                      py: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <Button size="small" onClick={handleCategoryCancel}>Cancel</Button>
                      <Button size="small" variant="contained" onClick={handleCategoryDone}>Done</Button>
                    </Box>
                  </ListSubheader>
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          {/* ── MODIFIED: Modal Save — disabled by add_user (add) or edit (edit) permission ── */}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              saving ||
              !formData.first_name.trim() ||
              !formData.mobile_number.trim() ||
              !formData.email.trim() ||
              !!errors.mobile_number ||
              !!errors.email ||
              (editingId ? !can('edit') : !can('add_user'))
            }
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          >
            {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default Users;